import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Divider,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  AutoAwesome,
  Close as CloseIcon,
  TrendingUp,
  Warning,
  AttachMoney,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Assessment,
  Analytics,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useCalWebSocket } from '../contexts/CalWebSocketContext';
import ReactMarkdown from 'react-markdown';

interface CalInsightButtonProps {
  componentType: string;
  data: any;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'inline';
  size?: 'small' | 'medium';
}

const CalInsightButton: React.FC<CalInsightButtonProps> = ({
  componentType,
  data,
  position = 'top-right',
  size = 'small',
}) => {
  const theme = useTheme();
  const { getIdTokenClaims } = useAuth0();
  const { sendMessage, isConnected, addMessageHandler } = useCalWebSocket();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [sessionId] = useState(`insights-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [queryId, setQueryId] = useState<string | null>(null);
  const [backendQueryId, setBackendQueryId] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState(() => {
    // Load saved font size from localStorage
    const saved = localStorage.getItem('calInsightsFontSize');
    return saved ? parseInt(saved, 10) : 15;
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (!insights && !loading) {
      fetchInsights();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const generatePrompt = () => {
    const basePrompt = "IMPORTANT: Provide a concise professional analysis in 2-5 sentences total. Be direct and actionable. You may use markdown formatting for emphasis (bold, italic, bullet points). Do not use emojis or special characters. Write with clear business language.\n\n";

    switch (componentType) {
      case 'top-10-loss':
        return basePrompt + `Analyze the top 10 loss contributors with total losses of $${data.totalLoss} across ${data.meters?.length || 0} meters. Focus on the pattern, severity, and which meters require immediate action to maximize recovery.`;

      case 'financial-matrix':
        return basePrompt + `Analyze the financial impact matrix showing ${data.highRiskCount} high-risk meters (high volume + high drift) with total exposure of $${data.totalExposure}. Identify the critical risk concentration and recommended mitigation strategy for the ${data.quadrants.highVolumeHighDrift} meters in the highest risk quadrant.`;

      case 'daily-trend':
        return basePrompt + `Analyze daily loss trend averaging $${data.averageDailyLoss} with ${data.trendDirection} direction. Project the annual impact if this trend continues and recommend specific actions to reverse negative trends.`;

      case 'product-distribution':
        return basePrompt + `Analyze loss distribution where ${data.topProduct} has the highest impact at $${data.topProductLoss} among ${data.meterCount} meters. Identify why this product concentration exists and recommend targeted remediation.`;

      case 'monthly-comparison':
        return basePrompt + `Analyze monthly performance showing ${data.changePercent}% change from $${data.previousMonth} to $${data.currentMonth}. Assess whether this trajectory is sustainable and what actions could improve the trend.`;

      case 'fleet-health':
        return basePrompt + `Analyze site health for ${data.totalMeters} meters at ${data.complianceRate}% compliance with ${data.criticalCount} critical and ${data.warningCount} warning states. Provide strategic assessment of overall health and top priority actions.`;

      case 'summary-stats':
        return basePrompt + `Analyze annual loss of $${data.annualLoss} from ${data.nonCompliantCount} non-compliant meters with ${data.averageOutsideControl || data.averageDrift}% average outside control limits. Calculate the ROI of fixing these meters and estimated payback period.`;

      case 'annual-exposure':
        return basePrompt + `Analyze annual exposure risk of $${data.annualExposure} from ${data.meterCount} meters averaging $${data.averageGain.toFixed(0)} per meter. Assess whether this exposure level is acceptable and what controls are needed.`;

      case 'annual-cost':
        return basePrompt + `Analyze annual non-compliance cost of $${data.totalCost} with losses at $${data.losses} and gains at $${data.gains}. Provide cost-benefit analysis of remediation efforts and prioritized recovery approach.`;

      case 'daily-losses':
        return basePrompt + `Analyze daily loss of $${data.dailyLoss} from ${data.meterCount} meters, annualizing to $${data.annualizedLoss}. Identify the urgency of addressing these daily losses and the cumulative impact if left unchecked.`;

      case 'daily-exposure':
        return basePrompt + `Analyze daily exposure of $${data.dailyExposure} from ${data.meterCount} meters, annualizing to $${data.annualizedExposure}. Assess the risk level of these daily gains and whether they indicate systemic measurement issues.`;

      case 'daily-cost':
        return basePrompt + `Analyze daily non-compliance cost of $${data.dailyCost} (losses: $${data.dailyLosses}, gains: $${data.dailyGains}), annualizing to $${data.annualizedCost}. Provide immediate actions to reduce daily impact and project monthly savings from remediation.`;

      case 'meter-specific':
        return basePrompt + `Analyze meter ${data.meterId} (${data.product}): Current factor ${data.meterFactor.toFixed(4)} with ${(data.percentOutsideControl || data.drift).toFixed(2)}% outside control limits, out of compliance for ${data.daysOut} days. Daily impact is $${Math.abs(data.dailyImpact).toFixed(0)} (annual: $${Math.abs(data.annualImpact).toFixed(0)}), direction: ${data.direction}. Provide specific remediation priority and expected payback period.`;

      default:
        return basePrompt + `Analyze this operational data and provide strategic insights for decision-making: ${JSON.stringify(data).substring(0, 200)}`;
    }
  };

  const fetchInsights = async () => {
    setLoading(true);
    setError('');

    try {
      const prompt = generatePrompt();
      
      if (isConnected) {
        // Use async WebSocket query for longer Cal responses
        const newQueryId = `insights-${componentType}-${Date.now()}`;
        setQueryId(newQueryId);

        // Send async query via WebSocket
        sendMessage(prompt, {
          action: 'calAsyncQuery',
          queryId: newQueryId,
          type: 'insights',
          component: componentType,
          session_id: sessionId,
        });

        // Response will be handled by the useEffect below
      } else {
        // Fallback to REST API with retry logic
        const tokenClaims = await getIdTokenClaims();
        const idToken = tokenClaims?.__raw;
        const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

        let retries = 2;
        let lastError;

        while (retries > 0) {
          try {
            const response = await fetch(`${API_ENDPOINT}/cal/chat`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify({
                message: prompt,
                sessionId,
                context: {
                  type: 'insights',
                  component: componentType,
                },
              }),
            });

            if (!response.ok) throw new Error('Failed to get insights');

            const result = await response.json();
            setInsights(result.response || 'No insights available');
            setLoading(false);
            return; // Success, exit the function
          } catch (error) {
            lastError = error;
            retries--;
            if (retries > 0) {
              // Wait a bit before retrying
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }

        // All retries failed
        throw lastError;
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError('Failed to load insights. Please try again.');
      setLoading(false);
    }
  };

  // Subscribe to WebSocket messages for async responses
  useEffect(() => {
    if (!queryId) return;

    const unsubscribe = addMessageHandler((message: any) => {
      // Store the backend's queryId after first status message
      if (message.type === 'status' && message.queryId && !backendQueryId) {
        // Check if this is a UUID (backend format) not our format
        if (!message.queryId.includes('insights-')) {
          setBackendQueryId(message.queryId);
        }
      }

      // Check if message matches either our queryId or the backend's queryId
      const isOurMessage = message.queryId === queryId || message.queryId === backendQueryId;

      if (isOurMessage && message.queryId) {
        if (message.type === 'status' && message.status === 'processing') {
          // Cal is processing
        } else if (message.type === 'complete') {
          const insightText = message.response || message.content || message.message || '';
          setInsights(insightText);
          setLoading(false);
          setQueryId(null);
          setBackendQueryId(null);
        } else if (message.type === 'error') {
          setError(message.message || 'Analysis failed');
          setLoading(false);
          setQueryId(null);
          setBackendQueryId(null);
        }
      }
    });

    return unsubscribe;
  }, [queryId, backendQueryId, addMessageHandler])

  const getPositionStyles = () => {
    switch (position) {
      case 'top-left':
        return { top: 8, left: 8 };
      case 'bottom-right':
        return { bottom: 8, right: 8 };
      case 'bottom-left':
        return { bottom: 8, left: 8 };
      default: // top-right
        return { top: 8, right: 8 };
    }
  };

  return (
    <>
      <Tooltip title="Get Cal Insights">
        <IconButton
          onClick={handleClick}
          size={size}
          sx={{
            ...(position !== 'inline' ? {
              position: 'absolute',
              ...getPositionStyles(),
            } : {}),
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
            zIndex: 10,
          }}
        >
          <AutoAwesome fontSize={size} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: { xs: 400, sm: 500 },
            maxWidth: '90vw',
            maxHeight: 500,
            overflow: 'auto',
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Paper sx={{ p: 2 }}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesome color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Cal Insights
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {/* Zoom Controls */}
              <Tooltip title="Zoom Out">
                <IconButton
                  size="small"
                  onClick={() => {
                    const newSize = Math.max(12, fontSize - 2);
                    setFontSize(newSize);
                    localStorage.setItem('calInsightsFontSize', newSize.toString());
                  }}
                  disabled={fontSize <= 12}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ mx: 1, minWidth: 35, textAlign: 'center' }}>
                {Math.round((fontSize / 15) * 100)}%
              </Typography>
              <Tooltip title="Zoom In">
                <IconButton
                  size="small"
                  onClick={() => {
                    const newSize = Math.min(24, fontSize + 2);
                    setFontSize(newSize);
                    localStorage.setItem('calInsightsFontSize', newSize.toString());
                  }}
                  disabled={fontSize >= 24}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Content */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          )}

          {error && (
            <Box sx={{ py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" color="error.main">
                <ErrorIcon />
                <Typography color="error">{error}</Typography>
              </Stack>
            </Box>
          )}

          {insights && !loading && !error && (
            <Box>
              {/* Parse insights with markdown support */}
              <Box sx={{
                '& p': {
                  fontSize: `${fontSize}px`,
                  lineHeight: 1.7,
                  marginBottom: '12px',
                  color: 'text.primary'
                },
                '& strong': {
                  fontWeight: 600,
                  color: 'text.primary'
                },
                '& em': {
                  fontStyle: 'italic',
                  color: 'text.primary'
                },
                '& ul, & ol': {
                  fontSize: `${fontSize}px`,
                  marginLeft: '20px',
                  marginBottom: '12px',
                  color: 'text.primary'
                },
                '& li': {
                  marginBottom: '4px',
                  color: 'text.primary'
                },
                '& code': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: `${fontSize - 1}px`,
                  color: theme.palette.primary.main
                },
                '& blockquote': {
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  paddingLeft: '12px',
                  marginLeft: 0,
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }
              }}>
                <ReactMarkdown>
                  {insights}
                </ReactMarkdown>
              </Box>

              {/* Component type indicator */}
              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="caption" color="text.secondary">
                  Analysis based on: {componentType.replace(/-/g, ' ').toUpperCase()}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Popover>
    </>
  );
};

export default CalInsightButton;